/** Наблюдения за безопасностью (КЧ-09): форма «Сообщить об опасности» для телефона и карточка с разбором */
import {
  OBSERVATION_CATEGORIES,
  OBSERVATION_MAX_PHOTOS,
  OBSERVATION_SEVERITIES,
  type ObservationCategory,
  type ObservationRow,
  type ObservationSeverity,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Camera } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date, dateTime, todayLocal } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, Spinner } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { KeyValue, Page, PageHeader, Panel } from '../../ui/layout';
import { useProjectDefault, useProjectsWith } from '../psd/shared';
import { ObservationBadge, ReasonDialog, usePermitPeople } from './safetyShared';

/** Отправка с телефона: проект, что увидели, фото, при желании анонимно. Кнопки не меньше 44 px */
export function ObservePage() {
  const search = useSearch({ strict: false }) as { project?: string };
  const qc = useQueryClient();
  const navigate = useNavigate();
  useCrumbs([{ label: 'Сообщить об опасности' }]);
  const { list } = useProjectsWith('project.view');
  const [f, setF] = useState({
    projectId: search.project ?? '',
    category: 'unsafe_condition' as ObservationCategory,
    place: '',
    description: '',
    anonymous: false,
  });
  const [photos, setPhotos] = useState<File[]>([]);
  const [sent, setSent] = useState<ObservationRow | null>(null);
  const setProject = useCallback((id: string) => setF((x) => ({ ...x, projectId: id })), []);
  useProjectDefault(list, search.project, f.projectId, setProject);
  const input = useRef<HTMLInputElement>(null);
  const send = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('projectId', f.projectId);
      form.append('category', f.category);
      if (f.place.trim()) form.append('place', f.place.trim());
      form.append('description', f.description.trim());
      form.append('anonymous', String(f.anonymous));
      for (const p of photos) form.append('photo', p, p.name);
      return api<ObservationRow>('/quality/observations', { method: 'POST', form });
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['qa-observations'] });
      setSent(r);
    },
  });
  if (sent)
    return (
      <Page>
        <PageHeader
          title={`Принято: ${sent.number}`}
          subtitle="Спасибо. Наблюдение ушло ответственному за охрану труда проекта."
        />
        <div className="flex flex-col gap-2 md:flex-row">
          <Button
            variant="primary"
            size="lg"
            className="h-12"
            onClick={() => {
              setSent(null);
              setPhotos([]);
              setF((x) => ({ ...x, description: '', place: '' }));
            }}
          >
            Сообщить ещё
          </Button>
          {!sent.anonymous ? (
            <Button
              variant="secondary"
              size="lg"
              className="h-12"
              onClick={() =>
                navigate({ to: '/qhse/observations/$observationId', params: { observationId: sent.id } })
              }
            >
              Открыть наблюдение
            </Button>
          ) : null}
        </div>
      </Page>
    );
  return (
    <Page className="max-w-2xl">
      <PageHeader
        title="Сообщить об опасности"
        subtitle="Сфотографируйте, что увидели, и коротко опишите. Можно анонимно."
      />
      {send.isError ? <ErrorBox error={send.error} className="mb-4" /> : null}
      <div className="flex flex-col gap-4">
        <Field label="Проект">
          <Select
            className="h-12"
            value={f.projectId}
            onChange={(e) => setF((x) => ({ ...x, projectId: e.target.value }))}
          >
            {list.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Что это">
          <Select
            className="h-12"
            value={f.category}
            onChange={(e) => setF((x) => ({ ...x, category: e.target.value as ObservationCategory }))}
          >
            {(Object.keys(OBSERVATION_CATEGORIES) as ObservationCategory[]).map((k) => (
              <option key={k} value={k}>
                {OBSERVATION_CATEGORIES[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Что вы увидели">
          <Textarea
            rows={4}
            value={f.description}
            onChange={(e) => setF((x) => ({ ...x, description: e.target.value }))}
          />
        </Field>
        <Field label="Где" hint="Оси, отметка, участок — по желанию">
          <Input
            className="h-12"
            value={f.place}
            onChange={(e) => setF((x) => ({ ...x, place: e.target.value }))}
          />
        </Field>
        <div className="flex flex-col gap-2">
          <input
            ref={input}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="hidden"
            onChange={(e) => {
              const add = Array.from(e.target.files ?? []);
              setPhotos((p) => [...p, ...add].slice(0, OBSERVATION_MAX_PHOTOS));
              if (input.current) input.current.value = '';
            }}
          />
          <Button
            variant="secondary"
            className="h-12"
            icon={<Camera className="size-5" />}
            disabled={photos.length >= OBSERVATION_MAX_PHOTOS}
            onClick={() => input.current?.click()}
          >
            {photos.length ? `Фото: ${photos.length} — добавить ещё` : 'Сфотографировать'}
          </Button>
          {photos.map((p, i) => (
            <span
              key={`${p.name}-${p.size}-${p.lastModified}`}
              className="flex items-center gap-2 text-[13px] text-ink-2"
            >
              <span className="min-w-0 flex-1 truncate">{p.name}</span>
              <button
                type="button"
                className="inline-flex min-h-11 items-center px-2 text-danger hover:underline"
                onClick={() => setPhotos((l) => l.filter((_, j) => j !== i))}
              >
                Убрать
              </button>
            </span>
          ))}
        </div>
        <label className="flex min-h-11 items-start gap-3 text-[14px] text-ink">
          <input
            type="checkbox"
            className="mt-1 size-5"
            checked={f.anonymous}
            onChange={(e) => setF((x) => ({ ...x, anonymous: e.target.checked }))}
          />
          <span>
            Отправить анонимно
            <span className="block text-[12px] text-ink-2">
              Портал не запишет, кто отправил. Но по месту, времени и данным внутри фото (модель телефона,
              место съёмки) автора иногда можно угадать — учитывайте это.
            </span>
          </span>
        </label>
        <Button
          variant="primary"
          className="h-12"
          loading={send.isPending}
          disabled={!f.projectId || f.description.trim().length < 3}
          onClick={() => send.mutate()}
        >
          Отправить
        </Button>
      </div>
    </Page>
  );
}

export function ObservationPage() {
  const { observationId } = useParams({ strict: false }) as { observationId: string };
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['qa-observation', observationId],
    queryFn: () => api<ObservationRow>(`/quality/observations/${observationId}`),
  });
  useCrumbs(q.data ? [{ label: q.data.number }] : null, q.error);
  const done = (d: ObservationRow) => {
    qc.setQueryData(['qa-observation', observationId], d);
    qc.invalidateQueries({ queryKey: ['qa-observations'] });
    qc.invalidateQueries({ queryKey: ['inbox'] });
    qc.invalidateQueries({ queryKey: ['tasks'] });
  };
  const [ask, setAsk] = useState<null | 'review' | 'done' | 'reject'>(null);
  const act = useMutation({
    mutationFn: (a: { path: string; body: unknown }) =>
      api<ObservationRow>(`/quality/observations/${observationId}/${a.path}`, { body: a.body }),
    onSuccess: (d) => {
      done(d);
      setAsk(null);
    },
  });
  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const d = q.data;
  return (
    <Page>
      <PageHeader
        title={`${d.number}: ${OBSERVATION_CATEGORIES[d.category]}`}
        subtitle={<ObservationBadge status={d.status} overdue={d.overdue} />}
        actions={
          <>
            {d.can.reject ? (
              <Button variant="ghost" size="lg" onClick={() => setAsk('reject')}>
                Отклонить
              </Button>
            ) : null}
            {d.can.review ? (
              <Button variant="primary" size="lg" onClick={() => setAsk('review')}>
                Разобрать
              </Button>
            ) : null}
            {d.can.done ? (
              <Button variant="primary" size="lg" onClick={() => setAsk('done')}>
                Меры выполнены
              </Button>
            ) : null}
          </>
        }
      />
      <Panel className="mb-6">
        <KeyValue
          items={[
            {
              label: 'Проект',
              value: (
                <Link
                  to="/projects/$projectId"
                  params={{ projectId: d.projectId }}
                  className="inline-flex min-h-11 items-center underline md:min-h-0"
                >
                  {d.projectCode}
                </Link>
              ),
            },
            { label: 'Что увидели', value: d.description },
            { label: 'Где', value: d.place ?? '—' },
            { label: 'Отправлено', value: dateTime(d.createdAt) },
            { label: 'Автор', value: d.anonymous ? 'Анонимно' : (d.authorName ?? '—') },
            ...(d.severity ? [{ label: 'Степень', value: OBSERVATION_SEVERITIES[d.severity] }] : []),
            ...(d.reviewNote ? [{ label: 'Заметка разбирающего', value: d.reviewNote }] : []),
            ...(d.measure
              ? [
                  {
                    label: 'Меры',
                    value: `${d.measure} · ${d.assigneeName ?? ''} · до ${date(d.measureDueDate)}`,
                  },
                ]
              : []),
            ...(d.closeNote ? [{ label: 'Итог', value: d.closeNote }] : []),
          ]}
        />
      </Panel>
      {d.photos.length ? (
        <Panel title="Фото">
          <ul className="flex flex-wrap gap-2">
            {d.photos.map((p) => (
              <li key={p.id}>
                <a
                  href={`/api/v1/files/${p.id}/download`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-h-11 items-center underline"
                >
                  {p.name}
                </a>
              </li>
            ))}
          </ul>
        </Panel>
      ) : (
        <Badge>Фото нет</Badge>
      )}
      {ask === 'review' ? (
        <ReviewDialog
          d={d}
          busy={act.isPending}
          error={act.error}
          onCancel={() => setAsk(null)}
          onSubmit={(body) => act.mutate({ path: 'review', body })}
        />
      ) : null}
      {ask === 'done' || ask === 'reject' ? (
        <ReasonDialog
          title={ask === 'done' ? 'Меры выполнены' : 'Отклонить наблюдение'}
          label={ask === 'done' ? 'Что сделано' : 'Причина'}
          confirm={ask === 'done' ? 'Закрыть' : 'Отклонить'}
          busy={act.isPending}
          error={act.error}
          onCancel={() => setAsk(null)}
          onConfirm={(t) =>
            act.mutate(
              ask === 'done' ? { path: 'done', body: { note: t } } : { path: 'reject', body: { reason: t } },
            )
          }
        />
      ) : null}
    </Page>
  );
}

function ReviewDialog({
  d,
  busy,
  error,
  onCancel,
  onSubmit,
}: {
  d: ObservationRow;
  busy: boolean;
  error: unknown;
  onCancel: () => void;
  onSubmit: (body: unknown) => void;
}) {
  const people = usePermitPeople(d.projectId);
  const [f, setF] = useState({
    severity: 'medium' as ObservationSeverity,
    note: '',
    measure: '',
    assigneeId: '',
    dueDate: todayLocal(new Date(Date.now() + 3 * 86_400_000)),
  });
  const withMeasure = f.measure.trim().length > 0;
  return (
    <Dialog
      open
      width="lg"
      onOpenChange={(v) => !v && onCancel()}
      title="Разобрать наблюдение"
      description="Без мер наблюдение закрывается «принято к сведению»."
      footer={
        <>
          <Button variant="ghost" className="h-11 md:h-10" onClick={onCancel}>
            Отмена
          </Button>
          <Button
            variant="primary"
            className="h-11 md:h-10"
            loading={busy}
            disabled={withMeasure && !f.assigneeId}
            onClick={() =>
              onSubmit({
                severity: f.severity,
                note: f.note.trim() || null,
                measure: withMeasure ? f.measure.trim() : null,
                assigneeId: withMeasure ? f.assigneeId : null,
                dueDate: withMeasure ? f.dueDate : null,
              })
            }
          >
            {withMeasure ? 'Назначить меры' : 'Принять к сведению'}
          </Button>
        </>
      }
    >
      {error ? <ErrorBox error={error} className="mb-3" /> : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Степень">
          <Select
            className="h-11 md:h-10"
            value={f.severity}
            onChange={(e) => setF((x) => ({ ...x, severity: e.target.value as ObservationSeverity }))}
          >
            {(Object.keys(OBSERVATION_SEVERITIES) as ObservationSeverity[]).map((k) => (
              <option key={k} value={k}>
                {OBSERVATION_SEVERITIES[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Заметка">
          <Input
            className="h-11 md:h-10"
            value={f.note}
            onChange={(e) => setF((x) => ({ ...x, note: e.target.value }))}
          />
        </Field>
        <Field label="Какие меры" className="sm:col-span-2">
          <Textarea
            rows={3}
            value={f.measure}
            onChange={(e) => setF((x) => ({ ...x, measure: e.target.value }))}
          />
        </Field>
        {withMeasure ? (
          <>
            <Field label="Кто выполнит">
              <Select
                className="h-11 md:h-10"
                value={f.assigneeId}
                onChange={(e) => setF((x) => ({ ...x, assigneeId: e.target.value }))}
              >
                <option value="">Выберите</option>
                {(people.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.fullName}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="К какому дню">
              <Input
                className="h-11 md:h-10"
                type="date"
                value={f.dueDate}
                onChange={(e) => setF((x) => ({ ...x, dueDate: e.target.value }))}
              />
            </Field>
          </>
        ) : null}
      </div>
    </Dialog>
  );
}
